'use strict';

//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************
//***********************************************************************************
//***********************************************************************************
//**** Misc Multi-Material Element
//***********************************************************************************
//***********************************************************************************
import { cn_element } from './cn_element';
import { cn_mmm_composition } from './cn_mmm_composition';
import { cn_add, cn_box, cn_dist, cn_middle, cn_mul, cn_normal, cn_normalize, cn_point_on_segment, cn_sub, cnx_clone } from '../utils/cn_utilities';
import { fh_polygon } from '@acenv/fh-3d-viewer';
import { CN_CURRENT_DATE } from '../utils/cn_transaction_manager';

export class cn_mmm_element extends cn_element {

    /**
     * @param {number[][]} vertices
     * @param {cn_mmm_composition} composition
     * @param {import('./cn_scene').cn_scene} parent
     */
    constructor(vertices, composition, parent) {
        super(parent);
        this.name = "";
        this.class_name = 'cn_mmm_element';
        this.vertices = vertices;
        this.width = 0.1;
        this.composition = composition;

        this.polyggon = null;
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************

    serialize() {
        let json = this.serialize_element();
        json.name = this.name;
        json.composition = this.composition.ID;
        json.vertices = this.vertices;
        json.width = this.width;
        return json;
    }

    /**
     * @param {any} json
     * @param {import('./cn_scene').cn_scene} scene
     * @returns {cn_mmm_element}
     */
    static unserialize(json, scene) {
        if (typeof (json.vertices) == 'undefined' || typeof (json.composition) == 'undefined') return null;
        let composition = scene.building.get_mmm_compositions().find(c => c.ID == json.composition);
        if (!composition) return null;

        let element = new cn_mmm_element(json.vertices, composition, scene);
        element.unserialize_element(json);
        element.vertices = json.vertices;


        if (typeof (json.width) == 'number')
            element.width = json.width;

        if (typeof (json.name) == 'string')
            element.name = json.name;

        scene.mmm_elements.push(element);
        return element;
    }

    //***********************************************************************************
    //**** draw
    //***********************************************************************************
    draw(camera, add_classes = [], fill = '') {
        let html = "";

        if (this.vertices.length == 0) return html;

        let extra = add_classes.join(' ');
        if (add_classes.indexOf('selected') >= 0) extra += " selected";
        if (add_classes.indexOf('mouseover') >= 0) extra += " mouseover";

        let screen_width = camera.world_to_screen_scale * this.width * 0.5;
        if (screen_width < 5) screen_width = 5;

        if (this.vertices.length == 1) {
            let c = camera.world_to_screen(this.vertices[0]);
            html += `<circle class="mmm ${extra}" cx="${c[0]}" cy="${c[1]}" r=${screen_width} />`;
            html += camera.draw_textured_point(this.ID, this.vertices[0]);
        }
        else if (this.vertices.length == 2) {
            let c0 = camera.world_to_screen(this.vertices[0]);
            let c1 = camera.world_to_screen(this.vertices[1]);
            if (cn_dist(c0, c1) < 10) {
                let c = cn_middle(c0, c1);
                html += `<circle class="mmm ${extra}" cx="${c[0]}" cy="${c[1]}" r=${screen_width} />`;
                html += camera.draw_textured_point(this.ID, cn_middle(this.vertices[0], this.vertices[1]));
            }
            else {
                let dir = cn_normal(cn_sub(c1, c0));
                cn_normalize(dir);
                dir = cn_mul(dir, screen_width);
                html += camera.draw_path_screen([cn_sub(c0, dir), cn_sub(c1, dir), cn_add(c1, dir), cn_add(c0, dir)], `mmm ${extra}`, true);
                html += camera.draw_textured_line(this.ID, this.vertices[0], this.vertices[1], true);
            }
        }
        else {
            html += camera.draw_path(this.vertices, `mmm ${extra}`, true, fill);
        }

        return html;
    }

    //***********************************************************************************
    /**
     * Returns bounding box
     * @returns {cn_box}
     */
    get_bounding_box() {
        let box = new cn_box();
        this.vertices.forEach(v => box.enlarge_point(v));
        return box;
    }

    //***********************************************************************************
    //**** Contains
    //***********************************************************************************
    contains(p, tolerance) {
        if (this.vertices.length == 1)
            return cn_dist(p, this.vertices[0]) <= Math.max(this.width * 0.5, tolerance);
        if (this.vertices.length == 2)
            return cn_point_on_segment(p, this.vertices[0], this.vertices[1], Math.max(this.width * 0.5, tolerance));

        if (!this.polygon || !this.up_to_date(this.polygon["date"], "vertices")) {
            this.polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
            this.polygon.add_contour(this.vertices.map(v => cnx_clone(v)));
            this.polygon.compute_contours();
            this.polygon["date"] = CN_CURRENT_DATE;
        }
        return this.polygon.contains(cnx_clone(p));
    }

    //***********************************************************************************
    //**** Contains
    //***********************************************************************************
    contained_by_box(box) {
        if (this.vertices.some(v => !box.contains_point(v))) return false;
        return true;
    }

}

